import { useEffect, useState } from "react";
import actions from "@Constants/actionsList";

// Interfaces
import { IPeriodTreeSelection } from "@/Layouts/components/SideBars/PayPeriodSideBar/interfaces";

// Hooks
import { useManagedPositions } from "@Common/hooks/Positions";
import { useAuthorization } from "@Common/hooks/Authorization/Authorization";

// Components
import TabSelector from "@Layouts/components/Tab/TabSelector";
import RoundedBox from "@Layouts/components/Basics/RoundedBox";
import PayRatesBody from "@DataPages/components/TimeAndPayRates/PayRates/PayRatesBody";
import PtoBody from "@DataPages/components/TimeAndPayRates/Pto/PtoBody";
import TimesCardsBody from "./TimesCards/TimeCardsBody";

// MUI
import { Grid, Skeleton, Stack, Typography } from "@mui/material";

// Images
import empty from "@/assets/images/commons/empty.png";
import EmptyBody from "@/Layouts/components/Basics/EmptyBody";

const generalTabs = ["Work Hours and Pay Rates", "Personal Time Off", "Time Cards"];

interface ITimeAndPayRatesBodyProps {
	selected: IPeriodTreeSelection;
	isUpdatable: boolean;
}

const TimeAndPayRatesBody = (props: Readonly<ITimeAndPayRatesBodyProps>) => {
	const [selected, setSelected] = useState<IPeriodTreeSelection>(null);

	// States
	const [indexTab, setIndexTab] = useState(0);

	// Permissions
	const { useAuth } = useAuthorization();
	const VIEW_PAY_RATES = useAuth(actions.VIEW_PAY_RATES);
	const VIEW_TIME_AND_ATTENDANCE = useAuth(actions.VIEW_TIME_AND_ATTENDANCE);
	const disabledTab = [!VIEW_PAY_RATES, !VIEW_TIME_AND_ATTENDANCE, false];

	const { data: managedPositions, isLoading: isLoadingPositions } = useManagedPositions({
		queryOpts: {
			staleTime: Infinity,
		},
	});

	const handleTabOnChange = (index: number) => {
		setIndexTab(index);
	};

	const renderTab = (tab: string) => {
		//Work Hours and Pay Rates", "Personal Time Off", "Time Cards"
		switch (tab) {
			case "Work Hours and Pay Rates":
				return (
					<PayRatesBody
						{...props}
						isAccessGranted={VIEW_PAY_RATES}
						managedPositions={managedPositions}
						isLoadingPositions={isLoadingPositions}
					/>
				);
			case "Personal Time Off":
				return (
					<PtoBody
						{...props}
						isAccessGranted={VIEW_TIME_AND_ATTENDANCE}
						managedPositions={managedPositions}
						isLoadingPositions={isLoadingPositions}
					/>
				);
			case "Time Cards":
				return <TimesCardsBody {...props} managedPositions={managedPositions ?? []} />;
			default:
				return null;
		}
	};

	useEffect(() => {
		setSelected(props.selected);
		return () => {
			setSelected(null);
		};
	}, [props.selected]);

	return (
		<RoundedBox sx={{ minHeight: "630px !important", padding: "0px !important" }}>
			{!selected ? (
				<EmptyBody
					image={empty}
					title={"Select a month to view the data"}
					bodyHeight={"480px"}
					sx={{
						boxShadow: "none !important",
					}}
				/>
			) : (
				<>
					<Grid item xs={12}>
						<TabSelector
							defaultTab={indexTab}
							readOnly={false}
							data={{
								disabledTab,
								tabs: generalTabs || [],
								topSwShow: false,
								topSwitchAdditional: false,
								bottomSwShow: false,
							}}
							contentProps={{ style: { marginTop: "62px" } }}
							tabValue={handleTabOnChange}
						/>
					</Grid>

					<Grid item xs={12} sx={{ height: "100%", padding: "30px !important" }}>
						<Grid item xs={12}>
							{generalTabs && (
								<>
									{selected?.cycleTitle ? (
										<Typography className={"page-title"} sx={{ lineHeight: "1.2" }}>
											{`${selected?.cycleTitle} ${selected?.title} ${generalTabs[indexTab]}`}
										</Typography>
									) : (
										<Skeleton variant="text" width={414} height={40} sx={{ marginTop: "-8px" }} />
									)}
									<Stack
										direction={"row"}
										spacing={4}
										justifyContent={"end"}
										sx={{ width: "100%", marginTop: "60px", position: "absolute", top: "77px", right: "48px" }}
									></Stack>
								</>
							)}
						</Grid>
						<Grid container mt={3} />
						{generalTabs && renderTab(generalTabs[indexTab])}
					</Grid>
				</>
			)}
		</RoundedBox>
	);
};

export default TimeAndPayRatesBody;
